"use client";

import { setLocaleNoRedirect } from "@/app/actions/set-locale";
import { LOCALE_COOKIE } from "@/lib/i18n/locales";
import { stripViPrefix } from "@/lib/i18n/localized-paths";

const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year, matching server action

/**
 * Writes the locale cookie directly via document.cookie so the value is
 * available to the browser immediately — before any async server round-trip
 * completes.
 *
 * This is the client-side half of the locale persistence strategy:
 * - server action: sets the HttpOnly=false cookie authoritatively via Set-Cookie
 * - this function: eagerly stamps the same cookie so any navigation that races
 *   ahead of the server action already carries the new locale in its Cookie header.
 *
 * The attribute set (path, max-age, samesite) intentionally matches
 * setLocaleNoRedirect in apps/web/src/app/actions/set-locale.ts.
 */
export function writeLocaleCookieClient(locale: string): void {
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; samesite=lax`;
}

/**
 * Persist the locale cookie for shared public routes, then force the browser to
 * rebuild the current unprefixed document from the updated cookie.
 *
 * Race-fix for VAL-I18N-003: the cookie is written on the client side
 * synchronously BEFORE the server action round-trip begins, so any navigation
 * that happens while the server action is in-flight (e.g. the user clicks a
 * nav link immediately after selecting a locale on /login) already carries the
 * new locale in its Cookie request header, preventing reversion to the old locale.
 *
 * The server action still runs to authoritatively set the cookie server-side
 * (overwriting the client-stamped value with an identical one), after which the
 * hard reload re-renders the current route with the new locale.
 */
export async function applyPublicLocaleSwitch(locale: string) {
  // Eagerly stamp the cookie on the client so racing navigation picks up the
  // new locale without waiting for the server action to complete.
  writeLocaleCookieClient(locale);

  const fd = new FormData();
  fd.set("locale", locale);
  await setLocaleNoRedirect(fd);
  reloadCurrentPublicRoute();
}

/**
 * Forces a document navigation to a public route so the destination request
 * re-reads the latest locale cookie instead of reusing a potentially stale
 * client-side route payload.
 */
export function navigateToPublicRoute(href: string) {
  window.location.assign(href);
}

export function reloadCurrentPublicRoute() {
  // On a /vi/... URL the URL would override the cookie just written, so go to
  // the English URL and let the cookie pick the language.
  const englishPath = stripViPrefix(window.location.pathname);
  if (englishPath === null) {
    window.location.reload();
    return;
  }
  window.location.assign(`${englishPath}${window.location.search}${window.location.hash}`);
}
